import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './QuickServices.module.scss';
import QuickServiceCard from './quick-services-card/QuickServicesCard';
import { MoreServicesModal } from './more-services-modal/MoreServicesModal';
import { primaryServiceItems, moreServiceItems } from './QuickServices.constants';
import { useFeatureFlags } from '../../../../tenant/features/useFeatureFlag';
import { ThreeDotsIcon } from '../../../../assets/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../stores/store';
import { PendingStatus } from '../../../../model/energy-profile';
import { ApplicationRoutes } from '../../../../routes';

interface QuickServicesProps {
  className?: string;
}

const QuickServices: React.FC<QuickServicesProps> = ({ className }) => {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const energyInfo = useSelector((state: RootState) => state.customerEnergyProfile);

  // Same rule as EnergyProfileGuard. When NOT_SET, every feature tile is locked
  // and tapping it routes the customer into energy profile setup.
  const hasValidEnergyProfile = !!energyInfo.utilityId && energyInfo.pendingStatus !== PendingStatus.Pending;
  const goToEnergyProfileSetup = () => navigate(`/${ApplicationRoutes.SETTINGS}/${ApplicationRoutes.ENERGY_PROFILE_WELCOME}`);

  // Gate quick-service tiles by feature flags (item.isShow reads the current flags).
  const { flags } = useFeatureFlags();
  const primaryItems = primaryServiceItems.filter(item => !item.isShow || item.isShow(flags ?? {}));
  const moreItems = moreServiceItems.filter(item => !item.isShow || item.isShow(flags ?? {}));

  return (
    <div className={`${styles.quickServices} ${className ?? ''}`}>
      <div className={styles.grid}>
        {primaryItems.map(item => (
          <QuickServiceCard
            key={item.id}
            icon={item.icon}
            title={t(item.title)}
            link={hasValidEnergyProfile ? item.link : undefined}
            isLocked={item.isLocked || !hasValidEnergyProfile}
            onClick={hasValidEnergyProfile ? item.onClick : goToEnergyProfileSetup}
          />
        ))}
        {moreItems.length > 0 && (
          <QuickServiceCard
            key="more-services"
            icon={<ThreeDotsIcon />}
            title={t('home.moreService')}
            isLocked={!hasValidEnergyProfile}
            onClick={hasValidEnergyProfile ? () => setShowModal(true) : goToEnergyProfileSetup}
          />
        )}
      </div>

      {showModal && (
        <MoreServicesModal
          items={moreItems}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default React.memo(QuickServices);
